import { useState, useCallback, useEffect } from 'react';
import type {
  WorkflowBlock, Connector, BlockGroup, BlockType, WorkflowState, PortSide,
  WorkflowSheet, MultiSheetWorkflowState,
} from '@/types/workflow';
import { isMultiSheetState, wrapSingleSheet, extractSheetState } from '@/types/workflow';
import { parseWorkflowState } from '@/lib/workflowSchema';
import { mergeIntoState, type MergeStateResult } from '@/lib/workflowMerge';
import {
  isPortPairAvailable, isCrossSheetPortPairAvailable,
} from '@/lib/connectorPortRules';
import { logger } from '@/lib/logger';
import { DEFAULT_WORKFLOW } from '@/constants/defaultWorkflow';
import { centerWorkflow, computeGroupBounds } from '@/lib/workflowCentering';
import { stripCirclePrefix } from '@/lib/sheetNaming';
import { ACTIVE_SHEET_KEY, snapToGrid, getDefaultBlockName, createEmptySheet, readStoredActiveSheet } from './multiSheetWorkflow/internals';
import { useUndoRedo } from './multiSheetWorkflow/useUndoRedo';

// S-102: GRID_SIZE måste vara 20 — CLAUDE.md-konvention + validateAiDiagram
// (`src/lib/aiDiagramTools.ts:421`) returnerar `validation-error` när
// `b.x % 20 !== 0`. Tidigare värde 10 lät desktop-drag landa på odda-10
// koordinater (10, 30, 50…) som AI extend-mode sedan inte kunde validera.
// Mobile fixad i S-027 (commit 4ccdbca); detta är desktop-counterparten.

/**
 * Multi-sheet workflow state management hook.
 *
 * Manages multiple sheets (tabs) with global undo/redo.
 * All per-sheet operations (createBlock, updateBlock, etc.) operate on the active sheet.
 * Cross-sheet connectors are stored at the workflow level.
 *
 * Compatible with the single-sheet useWorkflow API — downstream components
 * can consume `blocks`, `connectors`, `groups` directly (derived from active sheet).
 */

export function useMultiSheetWorkflow() {
  // Initialize from default workflow wrapped as single sheet
  const centeredDefault = centerWorkflow(DEFAULT_WORKFLOW);
  const wrapped: MultiSheetWorkflowState = wrapSingleSheet({
    ...centeredDefault,
    coordinateOrigin: 'center',
  });
  const initialState: MultiSheetWorkflowState = {
    ...wrapped,
    activeSheetIndex: readStoredActiveSheet(wrapped.sheets.length - 1),
  };

  const [state, setState] = useState<MultiSheetWorkflowState>(initialState);

  // E2E-only: when Playwright pre-seeds `localStorage['flow-weaver-workflow']`
  // and sets `window.__E2E_AUTO_LOAD__ = true` via addInitScript, hydrate
  // from that fixture on mount so tests run against a deterministic state.
  // Gated on DEV so this never runs in production bundles.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (typeof window === 'undefined') return;
    if (!(window as unknown as { __E2E_AUTO_LOAD__?: boolean }).__E2E_AUTO_LOAD__) return;
    try {
      const raw = localStorage.getItem('flow-weaver-workflow');
      if (!raw) return;
      const data = JSON.parse(raw);
      if (isMultiSheetState(data)) setState(data);
    } catch { /* ignore */ }
  }, []);

  // Persist active-sheet on every change. sessionStorage (not local) so
  // closing the tab resets — the assumption is that a new tab = new
  // viewing session and should start at sheet 0 unless URL says otherwise.
  useEffect(() => {
    try { sessionStorage.setItem(ACTIVE_SHEET_KEY, String(state.activeSheetIndex)); }
    catch { /* ignore storage-disabled browsers */ }
  }, [state.activeSheetIndex]);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);

  // ── Derived from active sheet ─────────────────────────────────
  const activeSheet = state.sheets[state.activeSheetIndex] ?? state.sheets[0];
  // Defensive: if a corrupted/foreign payload (e.g. an enterprise-tenant
  // config row whose `blocks` field is an object) ever reaches state, fall
  // back to empty arrays instead of crashing on `.find` / `.map`.
  const blocks = Array.isArray(activeSheet?.blocks) ? activeSheet.blocks : [];
  const connectors = Array.isArray(activeSheet?.connectors) ? activeSheet.connectors : [];
  const groups = Array.isArray(activeSheet?.groups) ? activeSheet.groups : [];
  const workflowName = state.name;

  // ── History (undo/redo) — extraherad till useUndoRedo (#5) ────
  const { undo, redo, canUndo, canRedo } = useUndoRedo(state, setState);

  // ── Sheet-level update helper ─────────────────────────────────
  const updateActiveSheet = useCallback((updater: (sheet: WorkflowSheet) => WorkflowSheet) => {
    setState(prev => ({
      ...prev,
      sheets: prev.sheets.map((s, i) => i === prev.activeSheetIndex ? updater(s) : s),
    }));
  }, []);

  // ── Sheet management ──────────────────────────────────────────
  const setActiveSheetIndex = useCallback((index: number) => {
    setState(prev => ({
      ...prev,
      activeSheetIndex: Math.max(0, Math.min(index, prev.sheets.length - 1)),
    }));
    setSelectedBlockId(null);
  }, []);

  const addSheet = useCallback((name?: string) => {
    // Defensive: only trust `name` if it's actually a non-empty string.
    // A React MouseEvent accidentally forwarded from an onClick would
    // otherwise end up stringified as the sheet name and corrupt state.
    //
    // `sheet.name` lagrar bara user-supplied TEXT — sifferprefix deriveras
    // från position vid render via getSheetDisplayName(). Strippa även
    // ev. prefix user råkar klistra in (defensive, idempotent).
    const trustedName = typeof name === 'string' && name.trim() ? stripCirclePrefix(name.trim()).trim() : '';
    setState(prev => ({
      ...prev,
      sheets: [...prev.sheets, createEmptySheet(trustedName)],
      activeSheetIndex: prev.sheets.length,
    }));
    setSelectedBlockId(null);
  }, []);

  const removeSheet = useCallback((index: number) => {
    setState(prev => {
      if (prev.sheets.length <= 1) return prev; // Must keep at least 1 sheet
      const newSheets = prev.sheets.filter((_, i) => i !== index);
      // Adjust cross-sheet connectors: remove any referencing deleted sheet, adjust indices
      const newCsc = prev.crossSheetConnectors
        .filter(c => c.fromSheetIndex !== index && c.toSheetIndex !== index)
        .map(c => ({
          ...c,
          fromSheetIndex: c.fromSheetIndex > index ? c.fromSheetIndex - 1 : c.fromSheetIndex,
          toSheetIndex: c.toSheetIndex > index ? c.toSheetIndex - 1 : c.toSheetIndex,
        }));
      return {
        ...prev,
        sheets: newSheets,
        crossSheetConnectors: newCsc,
        activeSheetIndex: Math.min(prev.activeSheetIndex, newSheets.length - 1),
      };
    });
    setSelectedBlockId(null);
  }, []);

  const renameSheet = useCallback((index: number, name: string) => {
    // Strippa ev. circle-prefix som user råkar skriva — sifferprefix är
    // FAST och deriveras från position. Lagra bara user-text.
    const cleanName = stripCirclePrefix(name).trim();
    setState(prev => ({
      ...prev,
      sheets: prev.sheets.map((s, i) => i === index ? { ...s, name: cleanName } : s),
    }));
  }, []);

  const reorderSheets = useCallback((fromIndex: number, toIndex: number) => {
    setState(prev => {
      if (fromIndex === toIndex) return prev;
      const newSheets = [...prev.sheets];
      const [moved] = newSheets.splice(fromIndex, 1);
      newSheets.splice(toIndex, 0, moved);

      // Remap cross-sheet connector indices
      const indexMap = new Map<number, number>();
      prev.sheets.forEach((_, oldI) => {
        let newI = oldI;
        if (oldI === fromIndex) newI = toIndex;
        else if (fromIndex < toIndex && oldI > fromIndex && oldI <= toIndex) newI = oldI - 1;
        else if (fromIndex > toIndex && oldI >= toIndex && oldI < fromIndex) newI = oldI + 1;
        indexMap.set(oldI, newI);
      });

      const newCsc = prev.crossSheetConnectors.map(c => ({
        ...c,
        fromSheetIndex: indexMap.get(c.fromSheetIndex) ?? c.fromSheetIndex,
        toSheetIndex: indexMap.get(c.toSheetIndex) ?? c.toSheetIndex,
      })).filter(c => c.fromSheetIndex !== c.toSheetIndex);

      let newActiveIndex = prev.activeSheetIndex;
      if (prev.activeSheetIndex === fromIndex) newActiveIndex = toIndex;
      else if (fromIndex < toIndex && prev.activeSheetIndex > fromIndex && prev.activeSheetIndex <= toIndex) newActiveIndex--;
      else if (fromIndex > toIndex && prev.activeSheetIndex >= toIndex && prev.activeSheetIndex < fromIndex) newActiveIndex++;

      return { ...prev, sheets: newSheets, crossSheetConnectors: newCsc, activeSheetIndex: newActiveIndex };
    });
  }, []);

  const duplicateSheet = useCallback((index: number) => {
    setState(prev => {
      const source = prev.sheets[index];
      if (!source) return prev;
      const idMap = new Map<string, string>();
      const newBlocks = source.blocks.map(b => {
        const newId = crypto.randomUUID();
        idMap.set(b.id, newId);
        return { ...b, id: newId };
      });
      const newConnectors = source.connectors
        .filter(c => idMap.has(c.fromId) && idMap.has(c.toId))
        .map(c => ({ ...c, id: crypto.randomUUID(), fromId: idMap.get(c.fromId)!, toId: idMap.get(c.toId)! }));
      // Groups: remap member block ids to the duplicate's new ids and
      // drop any dangling references (blockIds that didn't exist in the
      // source sheet anyway). Nesting is reset because parentGroupId
      // might reference a group outside the duplicated scope.
      const newGroups = source.groups.map(g => ({
        ...g, id: crypto.randomUUID(),
        blockIds: g.blockIds.map(bid => idMap.get(bid)).filter((v): v is string => !!v),
        parentGroupId: undefined,
      }));
      const newSheet: WorkflowSheet = {
        id: crypto.randomUUID(),
        name: `${source.name} (Copy)`,
        blocks: newBlocks,
        connectors: newConnectors,
        groups: newGroups,
        meta: source.meta ? { ...source.meta } : undefined,
      };
      const newSheets = [...prev.sheets];
      newSheets.splice(index + 1, 0, newSheet);
      return { ...prev, sheets: newSheets, activeSheetIndex: index + 1 };
    });
  }, []);

  const updateSheetMeta = useCallback((index: number, meta: Partial<WorkflowSheet['meta']>) => {
    setState(prev => ({
      ...prev,
      sheets: prev.sheets.map((s, i) => i === index ? { ...s, meta: { ...s.meta, ...meta } } : s),
    }));
  }, []);

  // ── Per-sheet block operations (active sheet) ─────────────────
  const createBlock = useCallback((type: BlockType, x: number, y: number): WorkflowBlock => {
    const block: WorkflowBlock = {
      id: crypto.randomUUID(), type,
      x: snapToGrid(x), y: snapToGrid(y),
      config: { name: getDefaultBlockName(type), description: '' },
      color: '#6366f1',
      mtime: Date.now(),
    };
    updateActiveSheet(s => ({ ...s, blocks: [...s.blocks, block] }));
    return block;
  }, [updateActiveSheet]);

  const updateBlock = useCallback((id: string, updates: Partial<WorkflowBlock>) => {
    updateActiveSheet(s => ({
      ...s, blocks: s.blocks.map(b => b.id === id ? { ...b, ...updates, mtime: Date.now() } : b),
    }));
  }, [updateActiveSheet]);

  const updateBlockPosition = useCallback((id: string, x: number, y: number, moveGroup?: boolean) => {
    updateActiveSheet(s => {
      const block = s.blocks.find(b => b.id === id);
      if (!block) return s;
      const now = Date.now();
      let newBlocks: WorkflowBlock[];
      if (moveGroup) {
        const group = s.groups.find(g => g.locked && g.blockIds.includes(id));
        if (group) {
          const dx = snapToGrid(x) - block.x;
          const dy = snapToGrid(y) - block.y;
          newBlocks = s.blocks.map(b =>
            group.blockIds.includes(b.id) ? { ...b, x: b.x + dx, y: b.y + dy, mtime: now } : b
          );
        } else {
          newBlocks = s.blocks.map(b => b.id === id ? { ...b, x: snapToGrid(x), y: snapToGrid(y), mtime: now } : b);
        }
      } else {
        newBlocks = s.blocks.map(b => b.id === id ? { ...b, x: snapToGrid(x), y: snapToGrid(y), mtime: now } : b);
      }
      // Update group bounds
      const newGroups = s.groups.map(g => {
        if (!g.blockIds.includes(id)) return g;
        const bounds = computeGroupBounds(g.blockIds, newBlocks);
        return bounds ? { ...g, ...bounds } : g;
      });
      return { ...s, blocks: newBlocks, groups: newGroups };
    });
  }, [updateActiveSheet]);

  const updateBlockIcon = useCallback((id: string, iconKey: string) => {
    updateActiveSheet(s => ({
      ...s, blocks: s.blocks.map(b => b.id === id ? { ...b, config: { ...b.config, icon: iconKey }, mtime: Date.now() } : b),
    }));
  }, [updateActiveSheet]);

  const deleteBlock = useCallback((blockId?: string) => {
    const id = blockId ?? selectedBlockId;
    if (!id) return;
    updateActiveSheet(s => ({
      ...s,
      blocks: s.blocks.filter(b => b.id !== id),
      connectors: s.connectors.filter(c => c.fromId !== id && c.toId !== id),
      groups: s.groups.map(g => ({
        ...g, blockIds: g.blockIds.filter(bid => bid !== id),
      })).filter(g => g.blockIds.length > 0 || g.collapsed),
    }));
    // Also remove cross-sheet connectors referencing this block on active sheet
    setState(prev => ({
      ...prev,
      crossSheetConnectors: prev.crossSheetConnectors.filter(c => {
        if (c.fromSheetIndex === prev.activeSheetIndex && c.fromBlockId === id) return false;
        if (c.toSheetIndex === prev.activeSheetIndex && c.toBlockId === id) return false;
        return true;
      }),
    }));
    setSelectedBlockId(null);
  }, [selectedBlockId, updateActiveSheet]);

  // ── Connector operations ──────────────────────────────────────
  /**
   * Toggle-create connector. Beteende:
   *   - Om ingen connector finns mellan endpointsen med samma port-par →
   *     skapa ny.
   *   - Om EXAKT samma (riktning + port-par, eller spegel-port-par) redan
   *     finns → ta bort den. Klickar user samma port-par två gånger är
   *     det alltså en remove — hur user tar bort linjer.
   *   - Self-loop med identisk fromPort/toPort kollapsar visuellt och
   *     blockeras explicit.
   *
   * Return-värdet signalerar vad som hände så callers kan logga rätt
   * ("Connection created" vs "Connection removed").
   */
  const createConnector = useCallback((
    fromId: string, toId: string, fromPort?: PortSide, toPort?: PortSide,
  ): 'created' | 'removed' | 'noop' | 'rejected' => {
    // Beslutet fattas UTANFÖR updateActiveSheet-closure:en eftersom React 18
    // StrictMode double-invokerar state-updaters och ett `let result`-capture
    // skulle ge fel värde vid andra-passeringen. Vi läser aktiv sheet från
    // state-closure:en här — createConnector är i use-deps på state.
    const activeSheet = state.sheets[state.activeSheetIndex];
    const existing = activeSheet?.connectors.find(c => {
      const sameEndpoints =
        (c.fromId === fromId && c.toId === toId) || (c.fromId === toId && c.toId === fromId);
      if (!sameEndpoints) return false;
      const forward = c.fromPort === fromPort && c.toPort === toPort;
      const reverse = c.fromPort === toPort && c.toPort === fromPort;
      return forward || reverse;
    });

    if (existing) {
      updateActiveSheet(s => ({
        ...s, connectors: s.connectors.filter(c => c.id !== existing.id),
      }));
      return 'removed';
    }
    // Self-loop med samma port på båda sidor kollapsar visuellt → noop.
    if (fromId === toId && (!fromPort || !toPort || fromPort === toPort)) {
      return 'noop';
    }
    // Port-uniqueness: per blockpar får varje (block, port) bara
    // användas en gång. UI-only — import/load tolererar legacy-data.
    if (!isPortPairAvailable(
      activeSheet?.connectors ?? [],
      fromId, toId, fromPort, toPort,
    )) {
      return 'rejected';
    }
    const connector: Connector = {
      id: crypto.randomUUID(), fromId, toId,
      fromPort: fromPort || 'right', toPort: toPort || 'left',
      mtime: Date.now(),
    };
    updateActiveSheet(s => ({ ...s, connectors: [...s.connectors, connector] }));
    return 'created';
  }, [state, updateActiveSheet]);

  const deleteConnector = useCallback((id: string) => {
    updateActiveSheet(s => ({ ...s, connectors: s.connectors.filter(c => c.id !== id) }));
  }, [updateActiveSheet]);

  const updateConnectorWaypoints = useCallback((id: string, waypoints: { x: number; y: number }[]) => {
    updateActiveSheet(s => ({
      ...s, connectors: s.connectors.map(c => c.id === id ? { ...c, waypoints, mtime: Date.now() } : c),
    }));
  }, [updateActiveSheet]);

  const updateConnectorLabel = useCallback((id: string, label: string) => {
    updateActiveSheet(s => ({
      ...s, connectors: s.connectors.map(c => c.id === id ? { ...c, label, mtime: Date.now() } : c),
    }));
  }, [updateActiveSheet]);

  /**
   * Generic patch over a connector — used by the connector properties
   * popover for color, bidirectional, label edits in one call. Stamps
   * mtime so realtime peers see the change.
   */
  const updateConnector = useCallback((id: string, patch: Partial<Connector>) => {
    updateActiveSheet(s => ({
      ...s,
      connectors: s.connectors.map(c => c.id === id ? { ...c, ...patch, mtime: Date.now() } : c),
    }));
  }, [updateActiveSheet]);

  // ── Group operations ──────────────────────────────────────────
  const createGroup = useCallback((blockIds: string[], color: string, parentGroupId?: string) => {
    updateActiveSheet(s => {
      if (blockIds.length < 2) return s;
      const newGroup: BlockGroup = {
        id: crypto.randomUUID(), blockIds, color,
        ...(parentGroupId ? { parentGroupId } : {}),
        mtime: Date.now(),
      };
      const bounds = computeGroupBounds(newGroup.blockIds, s.blocks);
      const withBounds = bounds ? { ...newGroup, ...bounds } : newGroup;
      return { ...s, groups: [...s.groups, withBounds] };
    });
  }, [updateActiveSheet]);

  const updateGroup = useCallback((id: string, updates: Partial<BlockGroup>) => {
    updateActiveSheet(s => ({
      ...s, groups: s.groups.map(g => g.id === id ? { ...g, ...updates, mtime: Date.now() } : g),
    }));
  }, [updateActiveSheet]);

  const updateGroups = useCallback((updater: (groups: BlockGroup[]) => BlockGroup[]) => {
    updateActiveSheet(s => ({ ...s, groups: updater(s.groups) }));
  }, [updateActiveSheet]);

  const deleteGroup = useCallback((groupId?: string) => {
    const id = groupId ?? selectedBlockId;
    if (!id) return;
    updateActiveSheet(s => {
      const group = s.groups.find(g => g.id === id);
      if (!group) return s;
      return {
        ...s,
        groups: s.groups.filter(g => g.id !== group.id),
        connectors: s.connectors.filter(c => c.fromId !== group.id && c.toId !== group.id),
      };
    });
  }, [selectedBlockId, updateActiveSheet]);

  const convertBlockToLayer = useCallback((blockId: string) => {
    updateActiveSheet(s => {
      const block = s.blocks.find(b => b.id === blockId);
      if (!block) return s;
      const parentGroup = s.groups.find(g => g.blockIds.includes(blockId));
      const newGroupId = crypto.randomUUID();
      const newGroup: BlockGroup = {
        id: newGroupId, blockIds: [], color: block.color,
        collapsed: true, collapsedX: block.x, collapsedY: block.y,
        name: block.config.name,
        ...(parentGroup ? { parentGroupId: parentGroup.id } : {}),
      };
      const newConnectors = s.connectors.map(c => ({
        ...c,
        fromId: c.fromId === blockId ? newGroupId : c.fromId,
        toId: c.toId === blockId ? newGroupId : c.toId,
      }));
      const newGroups = s.groups.map(g =>
        g.blockIds.includes(blockId)
          ? { ...g, blockIds: g.blockIds.filter(id => id !== blockId) }
          : g
      );
      return {
        ...s,
        blocks: s.blocks.filter(b => b.id !== blockId),
        connectors: newConnectors,
        groups: [...newGroups, newGroup],
      };
    });
  }, [updateActiveSheet]);

  // ── Cross-sheet connector operations ──────────────────────────
  // A-102: pre-flight validation före state-mutation. Strikare rejection
  // med typat result så callers kan rapportera specifik felorsak till user.
  const createCrossSheetConnector = useCallback((
    fromSheetIndex: number, toSheetIndex: number,
    fromBlockId: string, toBlockId: string,
    fromPort?: PortSide, toPort?: PortSide,
  ): 'created' | 'invalid-sheet-index' | 'non-adjacent' | 'dangling-from-block' | 'dangling-to-block' | 'port-in-use' => {
    // Guard: sheet-index måste vara ett giltigt array-index in nuvarande state.
    const sheetCount = state.sheets.length;
    if (
      fromSheetIndex < 0 || fromSheetIndex >= sheetCount ||
      toSheetIndex < 0 || toSheetIndex >= sheetCount
    ) {
      logger.warn('Cross-sheet connector rejected: invalid sheet-index', {
        fromSheetIndex, toSheetIndex, sheetCount,
      });
      return 'invalid-sheet-index';
    }
    // Ship 7 / Caveat 5A: adjacency constraint lifted. CrossSheetConnectors
    // may link ANY two distinct sheets in the workflow. Self-loops
    // (same sheet) are still rejected — those should be regular
    // intra-sheet connectors.
    if (fromSheetIndex === toSheetIndex) {
      logger.warn('Cross-sheet connectors cannot link a sheet to itself');
      return 'non-adjacent';
    }
    // Guard: block-id måste existera i respektive sheet innan vi refererar
    // det. Annars skapas dangling-connectors som alltid kommer rendera som
    // broken. Detta fångar race-conditions (block raderat mellan UI-val
    // och commit).
    if (!state.sheets[fromSheetIndex].blocks.some(b => b.id === fromBlockId)) {
      logger.warn('Cross-sheet connector rejected: from-block not found', { fromBlockId, fromSheetIndex });
      return 'dangling-from-block';
    }
    if (!state.sheets[toSheetIndex].blocks.some(b => b.id === toBlockId)) {
      logger.warn('Cross-sheet connector rejected: to-block not found', { toBlockId, toSheetIndex });
      return 'dangling-to-block';
    }
    // Port-uniqueness mellan samma block-par (cross-sheet, separat helper
    // eftersom (sheetIndex, blockId) krävs för identifikation).
    if (!isCrossSheetPortPairAvailable(
      state.crossSheetConnectors,
      fromSheetIndex, toSheetIndex, fromBlockId, toBlockId,
      fromPort, toPort,
    )) {
      logger.warn('Cross-sheet connector rejected: port already in use', {
        fromBlockId, toBlockId, fromPort, toPort,
      });
      return 'port-in-use';
    }
    setState(prev => ({
      ...prev,
      crossSheetConnectors: [...prev.crossSheetConnectors, {
        id: crypto.randomUUID(), fromSheetIndex, toSheetIndex,
        fromBlockId, toBlockId, fromPort, toPort,
      }],
    }));
    return 'created';
  }, [state.sheets, state.crossSheetConnectors]);

  const deleteCrossSheetConnector = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      crossSheetConnectors: prev.crossSheetConnectors.filter(c => c.id !== id),
    }));
  }, []);

  const getCrossSheetConnectorsForSheet = useCallback((sheetIndex: number) => {
    return state.crossSheetConnectors.filter(
      c => c.fromSheetIndex === sheetIndex || c.toSheetIndex === sheetIndex
    );
  }, [state.crossSheetConnectors]);

  // ── Canvas / workflow operations ──────────────────────────────
  const setWorkflowName = useCallback((name: string) => {
    setState(prev => ({ ...prev, name }));
  }, []);

  const clearCanvas = useCallback(() => {
    updateActiveSheet(() => createEmptySheet(activeSheet?.name ?? 'Sheet 1'));
    setSelectedBlockId(null);
  }, [updateActiveSheet, activeSheet?.name]);

  // ── Persistence ───────────────────────────────────────────────
  const getState = useCallback((): MultiSheetWorkflowState => state, [state]);

  const getActiveSheetState = useCallback((): WorkflowState => {
    return extractSheetState(state, state.activeSheetIndex);
  }, [state]);

  const loadState = useCallback((data: WorkflowState | MultiSheetWorkflowState) => {
    let multi: MultiSheetWorkflowState;
    if (isMultiSheetState(data)) {
      multi = data;
    } else {
      multi = wrapSingleSheet(data);
    }
    setState(multi);
    setSelectedBlockId(null);
  }, []);

  /**
   * Append-läge för "Import JSON". I motsats till loadState skriver
   * detta inte över canvas:en — innehållet mergas in via workflowMerge:
   *   - single-sheet imports → läggs till i aktiv sheet, offset:as
   *     ned-och-höger om current bbox + 200 px
   *   - multi-sheet imports → läggs till som nya flikar efter current
   * Allt importerat content wrapas i en "Imported"-grupp så user kan
   * dra hela batchen som en enhet. maxSheets kan klampa multi-sheet-
   * imports vid tier-gränsen — `truncated` + `droppedCrossLinks`
   * rapporteras till callern för toast-meddelanden.
   */
  const appendState = useCallback((
    data: WorkflowState | MultiSheetWorkflowState,
    opts?: { maxSheets?: number },
  ): Omit<MergeStateResult, 'state'> => {
    let result!: MergeStateResult;
    setState((prev) => {
      result = mergeIntoState(prev, data, opts);
      return result.state;
    });
    setSelectedBlockId(null);
    return {
      idMap: result.idMap,
      wrapperGroupIds: result.wrapperGroupIds,
      truncated: result.truncated,
      droppedCrossLinks: result.droppedCrossLinks,
    };
  }, []);

  const saveToLocalStorage = useCallback(() => {
    try {
      localStorage.setItem('flow-weaver-workflow', JSON.stringify(state));
    } catch (e) {
      logger.error('Failed to save to localStorage', e);
    }
  }, [state]);

  const loadFromLocalStorage = useCallback(() => {
    try {
      const raw = localStorage.getItem('flow-weaver-workflow');
      if (!raw) return;
      const data = JSON.parse(raw);
      if (isMultiSheetState(data)) {
        setState(data);
      } else {
        const parsed = parseWorkflowState(data);
        if (parsed) setState(wrapSingleSheet({
          name: parsed.name ?? '',
          blocks: (parsed.blocks ?? []) as WorkflowBlock[],
          connectors: (parsed.connectors ?? []) as Connector[],
          groups: (parsed.groups ?? []) as BlockGroup[],
          coordinateOrigin: parsed.coordinateOrigin,
        }));
      }
      setSelectedBlockId(null);
    } catch (e) {
      logger.error('Failed to load from localStorage', e);
    }
  }, []);

  const selectedBlock = blocks.find(b => b.id === selectedBlockId) || null;

  return {
    // Multi-sheet state
    state,
    sheets: state.sheets,
    activeSheetIndex: state.activeSheetIndex,
    crossSheetConnectors: state.crossSheetConnectors,

    // Sheet management
    setActiveSheetIndex,
    addSheet,
    removeSheet,
    renameSheet,
    reorderSheets,
    duplicateSheet,
    updateSheetMeta,

    // Cross-sheet connectors
    createCrossSheetConnector,
    deleteCrossSheetConnector,
    getCrossSheetConnectorsForSheet,

    // Active sheet data (backward compat with useWorkflow consumers)
    blocks,
    connectors,
    groups,
    selectedBlock,
    selectedBlockId,
    workflowName,
    setWorkflowName,
    setSelectedBlockId,

    // Per-sheet block operations
    createBlock,
    updateBlock,
    updateBlockPosition,
    updateBlockIcon,
    deleteBlock,

    // Per-sheet connector operations
    createConnector,
    deleteConnector,
    updateConnectorWaypoints,
    updateConnectorLabel,
    updateConnector,

    // Per-sheet group operations
    createGroup,
    updateGroup,
    updateGroups,
    deleteGroup,
    convertBlockToLayer,

    // Canvas
    clearCanvas,

    // Undo/redo
    undo,
    redo,
    canUndo,
    canRedo,

    // Persistence
    getState,
    getActiveSheetState,
    loadState,
    appendState,
    /**
     * Replace the currently active sheet's WorkflowState without touching
     * other sheets or cross-sheet connectors. Use this for AI-apply /
     * discard flows where `loadState(singleSheet)` would wipe every other
     * sheet via wrapSingleSheet.
     */
    updateActiveSheet,
    saveToLocalStorage,
    loadFromLocalStorage,
  };
}
